/**
 * <BeforeAfterFlow /> — the picture at the centre of the Deal Brief.
 *
 * Two process tracks on ONE shared column grid. Today's stages define the
 * columns; each Moxo stage sits directly beneath the stage(s) it replaces, so a
 * merge is a node that SPANS two columns and a removal is a visible hole in the
 * lower track. That geometry is the whole point: the reader sees the reduction
 * instead of reading a caption that asserts it. The alignment is resolved
 * server-side (lib/flow-align.ts) and is always drawable.
 *
 * Deliberately spare — nodes on a continuous rule (drawn as the rail's own
 * ::before, so the <ol> contains nothing but <li>) with labels beneath, not
 * cards with chrome. Everything is inline vector so it prints as part of the
 * PDF and inherits the theme in light and dark.
 */
import BriefIcon from './BriefIcon';
import type { CallBriefFlowAlignment, CallBriefFlowSide } from './call-brief-types';

function Node({
  icon,
  label,
  column,
  span,
  tone,
}: {
  icon: string;
  label: string;
  column: number;
  span: number;
  tone: 'today' | 'moxo';
}) {
  return (
    <li
      className={`db-track__node db-track__node--${tone}${span > 1 ? ' db-track__node--merged' : ''}`}
      style={{ gridColumn: `${column} / span ${span}` }}
    >
      <span className="db-track__dot">
        <BriefIcon name={icon} className="db-track__glyph" />
      </span>
      <span className="db-track__label">{label}</span>
    </li>
  );
}

export default function BeforeAfterFlow({
  before,
  after,
  alignment,
}: {
  before: CallBriefFlowSide;
  after: CallBriefFlowSide;
  alignment?: CallBriefFlowAlignment;
}) {
  if (!before?.steps?.length || !after?.steps?.length) return null;

  const columns = alignment?.columns || before.steps.length;
  // Without a resolved alignment (an older cached brief, or a share link
  // predating it) fall back to one column per stage — still drawable, just not
  // vertically corresponded.
  const spans =
    alignment?.after.length === after.steps.length
      ? alignment.after
      : after.steps.map((_, i) => ({ start: i + 1, end: i + 1 }));
  const removed = alignment?.removed ?? [];
  const style = { '--db-cols': columns } as React.CSSProperties;

  return (
    <figure className="db-track" data-testid="before-after-flow">
      <div className="db-track__row">
        <div className="db-track__lead">
          <span className="db-track__leadname">{before.title}</span>
          <span className="db-track__leadcount">
            {before.steps.length} step{before.steps.length === 1 ? '' : 's'}
          </span>
        </div>
        <ol className="db-track__rail db-track__rail--today" style={style}>
          {before.steps.map((step, i) => (
            <Node
              key={`today-${i}`}
              icon={step.icon}
              label={step.label}
              column={i + 1}
              span={1}
              tone="today"
            />
          ))}
        </ol>
        {before.caption && <p className="db-track__caption">{before.caption}</p>}
      </div>

      <div className="db-track__row db-track__row--moxo">
        <div className="db-track__lead">
          <span className="db-track__leadname">{after.title}</span>
          <span className="db-track__leadcount">
            {after.steps.length} step{after.steps.length === 1 ? '' : 's'}
          </span>
        </div>
        <ol className="db-track__rail db-track__rail--moxo" style={style}>
          {spans.map((span, i) => {
            const step = after.steps[i];
            if (!step) return null;
            return (
              <Node
                key={`moxo-${i}`}
                icon={step.icon}
                label={step.label}
                column={span.start}
                span={Math.max(1, span.end - span.start + 1)}
                tone="moxo"
              />
            );
          })}
          {/* A stage Moxo removes outright: a hole in the lower track, marked
              in the column where it used to be. */}
          {removed.map((col) => (
            <li key={`gone-${col}`} className="db-track__gone" style={{ gridColumn: col }}>
              <span className="db-track__gonemark" aria-hidden="true" />
              <span className="db-track__gonelabel">removed</span>
            </li>
          ))}
        </ol>
        {after.caption && <p className="db-track__caption">{after.caption}</p>}
      </div>
    </figure>
  );
}
